import React from 'react';

import Pill from 'carbon-react/lib/components/pill';

import { ExtendedForecastListItem } from '@futrli/views/processors/forecasts/forecastsList';
import { FormattedMessage, useIntl } from '@futrli/localisation';
import { getForecastDuration } from '@futrli/predict/forecast/forecast-duration';
import { ListingPageRowLayout } from '@futrli/ui-components/components/listing-page/listing-page-row/listing-page-row-layout/listing-page-row-layout';
import { ListingPageRowMetricCell } from '@futrli/ui-components/components/listing-page/listing-page-row/listing-page-row-metric-cell/listing-page-row-metric-cell';
import { ListingPageRowNameCell } from '@futrli/ui-components/components/listing-page/listing-page-row/listing-page-row-name-cell/listing-page-row-name-cell';
import { ResourceAccessLabel } from '@futrli/predict/sharing/components/resource-access-label/resource-access-label';

import { MyForecastRowActions } from '../my-forecast-row-actions/my-forecast-row-actions';

export const ROW_HEIGHT_DEFAULT = 78;
export const ROW_HEIGHT_WITH_LOCKED_PILL = 106;

const getStatusBadge = (
  forecast: ExtendedForecastListItem,
  intl: ReturnType<typeof useIntl>,
  isResourceSharingEnabled: boolean
) => {
  if (isResourceSharingEnabled) {
    if (forecast.permission_info.sharing_status === 'shared') {
      return (
        <Pill colorVariant="positive" size="S" fill>
          {intl.formatMessage({ id: 'common.shared', defaultMessage: 'Shared' })}
        </Pill>
      );
    }

    return (
      <Pill size="S" pillRole="status">
        {intl.formatMessage({ id: 'common.private', defaultMessage: 'Private' })}
      </Pill>
    );
  }

  return forecast.status === 'published' ? (
    <Pill colorVariant="positive" size="S" fill>
      {intl.formatMessage({ id: 'common.published', defaultMessage: 'Published' })}
    </Pill>
  ) : (
    <Pill size="S" pillRole="status">
      {intl.formatMessage({ id: 'common.draft', defaultMessage: 'Draft' })}
    </Pill>
  );
};

interface MyForecastRowProps {
  forecast: ExtendedForecastListItem;
  onNameClick: (forecastId: string) => void;
  isResourceSharingEnabled?: boolean;
}

export const MyForecastRow = ({ forecast, onNameClick, isResourceSharingEnabled = false }: MyForecastRowProps) => {
  const intl = useIntl();

  const createdDate = intl.formatDate(forecast.created, { day: '2-digit', month: 'short', year: 'numeric' });
  const duration = getForecastDuration(forecast);
  const durationLabel = intl.formatMessage(
    {
      id: 'predict.forecasts.duration_years',
      defaultMessage: '{duration, plural, one {# year} other {# years}}'
    },
    { duration }
  );
  const startDate = forecast.window_from
    ? intl.formatDate(forecast.window_from, { day: '2-digit', month: 'short', year: 'numeric' })
    : undefined;

  const getForecastKindLabel = (forecast: ExtendedForecastListItem) => {
    if (forecast.kind === 'budget') {
      return intl.formatMessage({ id: 'predict.forecasts.label.budget', defaultMessage: 'P&L budget' });
    }
    return intl.formatMessage({
      id: 'predict.forecasts.label.three_way_forecast',
      defaultMessage: '3-way forecast'
    });
  };

  return (
    <ListingPageRowLayout
      nameColumn={
        <ListingPageRowNameCell
          name={forecast.name}
          onNameClick={() => onNameClick(forecast.id)}
          subtext={getForecastKindLabel(forecast)}
          pill={
            forecast.is_frozen ? (
              <Pill pillRole="status" size="S" mt="2px">
                {intl.formatMessage({ id: 'common.locked', defaultMessage: 'Locked' })}
              </Pill>
            ) : undefined
          }
        />
      }
      metrics={
        <>
          <ListingPageRowMetricCell
            label={<FormattedMessage id="common.status" defaultMessage="Status" />}
            value={getStatusBadge(forecast, intl, isResourceSharingEnabled)}
          />
          {isResourceSharingEnabled && (
            <ListingPageRowMetricCell
              label={<FormattedMessage id="common.access" defaultMessage="Access" />}
              value={<ResourceAccessLabel permission={forecast.permission_info?.effective_permission} />}
            />
          )}
          <ListingPageRowMetricCell
            label={<FormattedMessage id="common.start_date" defaultMessage="Start date" />}
            value={startDate}
          />
          <ListingPageRowMetricCell
            label={<FormattedMessage id="predict.forecasts.duration" defaultMessage="Duration" />}
            value={durationLabel}
          />
          <ListingPageRowMetricCell
            label={<FormattedMessage id="common.date_created" defaultMessage="Date created" />}
            value={createdDate}
          />
          <ListingPageRowMetricCell
            size="wide"
            label={<FormattedMessage id="common.owner" defaultMessage="Owner" />}
            value={
              forecast.isOwner ? (
                <FormattedMessage id="common.owner.you" defaultMessage="You" />
              ) : (
                forecast.ownerName || '—'
              )
            }
          />
        </>
      }
      actions={<MyForecastRowActions forecast={forecast} />}
    />
  );
};
